// tabs/NotesTab.tsx
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Center, Loader, Stack, Text, Group, ActionIcon, Tooltip,
  Checkbox, Textarea, Button, ScrollArea, Box,
} from '@mantine/core';
import ChatBubble from '@/components/advanced/communication/ChatBubble';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useRegistrationService,
  useUserService,
  RegistrationNote,
  CreateRegistrationNote,
  UpdateRegistrationNoteRequest,
} from '@actschurch/ccms-sdk';
import ConfirmActionModal from './ConfirmActionModal';
import { IconRefresh, IconSend } from '@tabler/icons-react';

interface NotesTabProps {
  registrationId: number;
  active: boolean;
  currentUserId?: string;
}

interface NoteItem {
  id: string;
  noteID: number;
  authorId: string;
  authorName: string;
  message: string;
  timestamp: string;
  isEdited: boolean;
  hideFromFacilitator: boolean;
}

function sdkDateToISO(value: number | string): string {
  const raw = String(value ?? '');
  if (raw.length < 14) return new Date().toISOString();
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}T${raw.slice(8, 10)}:${raw.slice(10, 12)}:${raw.slice(12, 14)}`;
}

function decodeNote(value: string): string {
  try {
    return decodeURIComponent(escape(atob(value)));
  } catch {
    return value ?? '';
  }
}

function encodeNote(value: string): string {
  try {
    return btoa(unescape(encodeURIComponent(value)));
  } catch {
    return value;
  }
}

export default function NotesTab({ registrationId, active, currentUserId }: NotesTabProps) {
  const registrationService = useRegistrationService();
  const userService = useUserService();

  const [notes, setNotes] = useState<NoteItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);

  const [draft, setDraft] = useState('');
  const [hideFromFacilitator, setHideFromFacilitator] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const nameCache = useRef<Map<string, string>>(new Map());

  const resolveName = useCallback(async (userId: string): Promise<string> => {
    if (!userId) return 'Staff';
    if (nameCache.current.has(userId)) return nameCache.current.get(userId)!;
    try {
      const res: any = await userService.getById(userId);
      const user = res?.data ?? res;
      if (user) {
        const name =
          [user.firstName, user.lastName ?? user.surname].filter(Boolean).join(' ').trim() ||
          user.displayName?.trim() ||
          user.email ||
          userId;
        nameCache.current.set(userId, name);
        return name;
      }
    } catch {
      // fall through
    }
    nameCache.current.set(userId, userId);
    return userId;
  }, [userService]);

  const noteToItem = useCallback(async (note: RegistrationNote): Promise<NoteItem> => {
    // Admin author (adminUserId) vs facilitator author (userId) — resolve
    // whichever is present so multiple admins/facilitators are distinguishable.
    const authorId = note.adminUserId ?? note.userId ?? '';
    let authorName = 'Unknown';
    if (note.adminUserId) {
      authorName = `${await resolveName(note.adminUserId)}`;
    } else if (note.userId) {
      authorName = await resolveName(note.userId);
    }
    return {
      id: String(note.noteID),
      noteID: note.noteID,
      authorId,
      authorName,
      message: decodeNote(note.note ?? ''),
      timestamp: sdkDateToISO(note.entryDateTime),
      isEdited: !!note.modifiedDateTime && note.modifiedDateTime !== note.createdDateTime,
      hideFromFacilitator: note.hideFromFacilitator,
    };
  }, [resolveName]);

  const fetchNotes = useCallback(async () => {
    if (!registrationId) return;
    setLoading(true);
    try {
      const raw: RegistrationNote[] = await registrationService.getNotes(registrationId);
      const sorted = [...(raw ?? [])].sort((a, b) => a.entryDateTime - b.entryDateTime);
      const resolved = await Promise.all(sorted.map(noteToItem));
      setNotes(resolved);
    } catch (e) {
      console.error('Error fetching notes:', e);
      toast.error('Error', 'Could not load notes.');
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [registrationId, registrationService, noteToItem]);

  useEffect(() => { if (active && notes === null) fetchNotes(); }, [active, notes, fetchNotes]);

  const [currentUserName, setCurrentUserName] = useState('You');
  useEffect(() => {
    if (!currentUserId) return;
    resolveName(currentUserId).then(setCurrentUserName);
  }, [currentUserId, resolveName]);

  const handlePost = async () => {
    const content = draft.trim();
    if (!content) return;
    setPosting(true);
    try {
      const data: CreateRegistrationNote = {
        registrationID: registrationId,
        note: encodeNote(content),
        hideFromFacilitator,
      };
      await registrationService.addNote(registrationId, data);
      setDraft('');
      setHideFromFacilitator(false);
      await fetchNotes();
      toast.success('Note Added', 'Your note has been saved.');
    } catch (e) {
      console.error('Error adding note:', e);
      toast.error('Error', 'Could not save note. Please try again.');
    } finally {
      setPosting(false);
    }
  };

  const startEdit = (item: NoteItem) => {
    setEditingId(item.id);
    setEditDraft(item.message);
  };

  const handleSaveEdit = async (item: NoteItem) => {
    const content = editDraft.trim();
    if (!content) return;
    try {
      const data: UpdateRegistrationNoteRequest = { note: encodeNote(content) };
      await registrationService.updateNote(registrationId, item.noteID, data);
      setEditingId(null);
      setEditDraft('');
      await fetchNotes();
      toast.success('Note Updated', 'Your note has been updated.');
    } catch (e) {
      console.error('Error updating note:', e);
      toast.error('Error', 'Could not update note. Please try again.');
    }
  };

  const handleDeleteComment = (id: string) => {
    setDeleteTargetId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDeleteComment = async () => {
    const id = deleteTargetId;
    if (!id) {
      setDeleteModalOpen(false);
      return;
    }
    const noteId = Number(id);
    if (!noteId) {
      setDeleteModalOpen(false);
      setDeleteTargetId(null);
      return;
    }

    setDeleting(true);
    try {
      await registrationService.deleteNote(registrationId, noteId);
      toast.warning('Note Deleted', 'Note removed.');
      await fetchNotes();
    } catch (e) {
      console.error('Error deleting note:', e);
      toast.error('Error', 'Could not delete note. Please try again.');
    } finally {
      setDeleting(false);
      setDeleteModalOpen(false);
      setDeleteTargetId(null);
    }
  };

  if (notes === null || (loading && notes.length === 0)) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">Loading notes…</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <>
      <Group justify="right" mb="md">
        <Tooltip label="Refresh notes" withArrow>
          <ActionIcon variant="subtle" onClick={fetchNotes} loading={loading} size="lg">
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <Stack gap="md">
        {/* `mih` as well as `mah`: without a floor the thread collapses to the height of its
            content, so an empty or one-note registration left the composer jammed under the toolbar. */}
        <ScrollArea.Autosize mah={480} mih={320} type="auto">
          <Stack gap={4} py="xs" style={{ minHeight: 320 }}>
            {notes.length === 0 && (
              <Center py="lg" style={{ flex: 1 }}>
                <Text size="sm" c="dimmed">No notes yet.</Text>
              </Center>
            )}
            {notes.map((item) => {
              const isOwn = !!currentUserId && item.authorId === currentUserId;

              if (editingId === item.id) {
                return (
                  <Box key={item.id} maw="70%" ml={isOwn ? 'auto' : 0} mr={isOwn ? 0 : 'auto'}>
                    <Textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.currentTarget.value)}
                      autosize
                      minRows={2}
                    />
                    <Group gap="xs" justify="flex-end" mt={4}>
                      <Button size="xs" variant="subtle" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                      <Button size="xs" onClick={() => handleSaveEdit(item)}>
                        Save
                      </Button>
                    </Group>
                  </Box>
                );
              }

              return (
                <Box key={item.id} maw="70%" ml={isOwn ? 'auto' : 0} mr={isOwn ? 0 : 'auto'}>
                  <Text size="xs" fw={600} ta={isOwn ? 'right' : 'left'} mb={2} px={4}>
                    {item.authorName}
                    {item.hideFromFacilitator && (
                      <Text component="span" size="xs" c="dimmed" fw={400} ml={6}>
                        (hidden from facilitator)
                      </Text>
                    )}
                  </Text>
                  <ChatBubble
                    message={item.message}
                    sender={{ name: item.authorName }}
                    timestamp={item.timestamp}
                    // Always render the readable/colored bubble style; real
                    // ownership still drives alignment above and edit/delete
                    // permissions below.
                    isOwn
                    isEdited={item.isEdited}
                    showAvatar={false}
                    onEdit={isOwn ? () => startEdit(item) : undefined}
                    onDelete={isOwn ? () => handleDeleteComment(item.id) : undefined}
                    onCopy={() => {
                      navigator.clipboard.writeText(item.message);
                      toast.success('Copied', 'Note copied to clipboard.');
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
        </ScrollArea.Autosize>

        <Stack gap="xs">
          <Textarea
            placeholder="Write a note…"
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            autosize
            minRows={2}
            maxRows={6}
          />
          <Group justify="space-between">
            <Checkbox
              label="Hide from facilitator"
              checked={hideFromFacilitator}
              onChange={(e) => setHideFromFacilitator(e.currentTarget.checked)}
            />
            <Button
              leftSection={<IconSend size={14} />}
              onClick={handlePost}
              loading={posting}
              disabled={!draft.trim()}
            >
              Post
            </Button>
          </Group>
        </Stack>
      </Stack>

      <ConfirmActionModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete note"
        message="Delete this note? This cannot be undone."
        color="red"
        onConfirm={handleConfirmDeleteComment}
      />
    </>
  );
}